if (!customElements.get('backside-engraving-modal')) {
  customElements.define(
    'backside-engraving-modal',
    class BacksideEngravingModal extends HTMLElement {
      constructor() {
        super()
        this.sectionId = this.dataset.sectionId
        this.formId = this.dataset.productFormId
        this.buttonTextWhenYes = this.dataset.buttonTextWhenYes || 'Add Engraving Details'
        this.addToCartText = this.dataset.addToCartText || 'Add to cart'
        this.moneyFormat = this.dataset.moneyFormat || '{{amount}}'
        this.variantPriceMap = this.buildVariantPriceMap()
        this.boundClose = this.close.bind(this)
        this.boundKeydown = this.onKeydown.bind(this)
        this.boundFormSubmit = this.onFormSubmit.bind(this)
      }

      buildVariantPriceMap() {
        const ids = (this.dataset.variantIds || '').split(',').filter(Boolean)
        const prices = (this.dataset.variantPrices || '').split(',').filter(Boolean)
        const map = {}
        ids.forEach((id, i) => {
          map[id.trim()] = parseInt(prices[i]?.trim() || '0', 10)
        })
        return map
      }

      /**
       * Build option config from DOM: one entry per [data-engraving-card].
       * Keys: variant ID when present, otherwise "index-{upsellIndex}". Value: { type, requiredFilesCount, hasText, hasFont }.
       */
      buildOptionConfigs() {
        const configs = {}
        this.querySelectorAll('[data-engraving-card]').forEach(card => {
          const variantId = String(card.dataset.variantId || '').trim()
          const upsellIndex = card.dataset.upsellIndex || ''
          const optionId = variantId || (upsellIndex ? `index-${upsellIndex}` : null)
          if (!optionId) return
          const type = (card.dataset.upsellType || '').trim()
          const requiredFilesCount =
            type === 'fingerprint_heart'
              ? 2
              : type === 'handwriting' || type === 'fingerprint'
                ? 1
                : 0
          const hasText = type === 'personal_text'
          const hasFont = type === 'personal_text' && !!card.querySelector('[data-engraving-font]')
          configs[optionId] = { type, requiredFilesCount, hasText, hasFont }
        })
        return configs
      }

      /** Resolve option id to the card element (by variant id or index-{n}). */
      getCardByOptionId(optionId) {
        if (!optionId) return null
        const root = this.overlay || this
        const byVariant = root.querySelector(`[data-engraving-card][data-variant-id="${optionId}"]`)
        if (byVariant) return byVariant
        if (String(optionId).startsWith('index-')) {
          const index = String(optionId).slice(6)
          return root.querySelector(`[data-engraving-card][data-upsell-index="${index}"]`) || null
        }
        return null
      }

      /**
       * Read current values from a card's inputs (text, font, file count). Used for validation only.
       * Only first text field is required.
       */
      getValuesFromCard(card) {
        if (!card)
          return { text: '', font: '', fileCount: 0, uploadLaterChecked: false, slotFileCounts: [] }
        const requiredTextEl = card.querySelector(
          '[data-engraving-text][data-engraving-text-required]'
        )
        const fontEl = card.querySelector('[data-engraving-font]')
        const uploadLaterEl = card.querySelector('[data-engraving-upload-later]')
        const uploadLaterChecked = uploadLaterEl?.checked || false

        const section = card.querySelector('[data-engraving-file-section]')
        let fileCount = 0
        const slotFileCounts = []
        if (section) {
          section.querySelectorAll('[data-engraving-file-slot]').forEach(slot => {
            const propBase = (slot.dataset.propBase || '').trim()
            let count = 0
            const hiddenContainer = section.querySelector('[data-engraving-hidden-files]')
            if (hiddenContainer) {
              hiddenContainer.querySelectorAll('input[type="file"]').forEach(inp => {
                const name = inp.getAttribute('name') || ''
                if (name.includes(propBase) && inp.files && inp.files.length > 0) count++
              })
            }
            if (count === 0) {
              const fileInput = slot.querySelector('input[data-engraving-file]')
              if (fileInput?.files?.length) count = fileInput.files.length
            }
            slotFileCounts.push(count)
            fileCount += count
          })
        } else {
          card.querySelectorAll('input[data-engraving-file]').forEach(input => {
            if (input.files && input.files.length > 0) fileCount += 1
          })
        }
        return {
          text: (requiredTextEl?.value || '').trim(),
          font: (fontEl?.value || '').trim(),
          fileCount,
          uploadLaterChecked,
          slotFileCounts
        }
      }

      /**
       * Validate values against an option config. Returns true only when all required fields are satisfied.
       */
      validateOption(config, values) {
        if (!config) return false
        if (config.hasText && !values.text) return false
        if (config.hasFont && !values.font) return false
        if (config.requiredFilesCount > 0 && !values.uploadLaterChecked) {
          if (config.type === 'fingerprint_heart' && values.slotFileCounts?.length >= 2) {
            if (values.slotFileCounts[0] < 1 || values.slotFileCounts[1] < 1) return false
          } else if (values.fileCount < config.requiredFilesCount) {
            return false
          }
        }
        return true
      }

      /**
       * True if the given option (variant id or index-{n}) has valid values. Only the selected card's validity controls CTA.
       */
      getIsOptionValid(optionId) {
        if (!optionId) return false
        const card = this.getCardByOptionId(optionId)
        if (!card) return false
        const config = this.optionConfigs[optionId]
        if (!config) return false
        const values = this.getValuesFromCard(card)
        return this.validateOption(config, values)
      }

      /** CTA is enabled when no option selected (add main only) OR when selected option has valid values. */
      get isSubmitEnabled() {
        if (this.selectedOptionId == null) return true
        return this.getIsOptionValid(this.selectedOptionId)
      }

      formatMoney(cents) {
        if (cents == null || isNaN(cents)) return ''
        const amount = (cents / 100).toFixed(2)
        const amountComma = amount.replace('.', ',')
        return this.moneyFormat
          .replace(/\{\{\s*amount\s*\}\}/gi, amount)
          .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/gi, amountComma)
          .replace(/\{\{\s*amount_no_decimals\s*\}\}/gi, Math.round(cents / 100).toString())
          .trim()
      }

      updateModalButtonText() {
        const btnTextEl = (this.overlay || this).querySelector('[data-engraving-button-text]')
        if (!btnTextEl) return
        const form = document.getElementById(this.formId)
        const mainVariantId = form?.querySelector('input[name="id"]')?.value
        const mainCents = mainVariantId ? this.variantPriceMap[mainVariantId] : 0
        const engravingCents = this.selectedCard?.dataset?.priceCents
          ? parseInt(this.selectedCard.dataset.priceCents, 10)
          : 0
        const totalCents = (mainCents || 0) + (engravingCents || 0)
        const formatted = totalCents > 0 ? this.formatMoney(totalCents) : ''
        btnTextEl.textContent = formatted
          ? `${this.addToCartText} | ${formatted}`
          : `${this.addToCartText} | —`
      }

      connectedCallback() {
        this.toggleInputs = this.querySelectorAll('[data-engraving-toggle]')
        this.overlay = this.querySelector('[data-engraving-overlay]')
        this.closeBtn = this.querySelector('[data-engraving-close]')
        this.optionConfigs = this.buildOptionConfigs()
        this.selectedOptionId = null

        this.realSubmitButton = document.getElementById(`ProductSubmitButton-${this.sectionId}`)
        if (!this.realSubmitButton) return

        this.createCloneButton()

        this.toggleInputs.forEach(input => {
          input.addEventListener('change', () => this.handleToggleChange(input))
        })

        const section = this.closest('section')
        if (section) {
          this.boundFileUploadUpdated = () => this.updateButtonVisibility()
          section.addEventListener(
            'optional-file-upload-button-updated',
            this.boundFileUploadUpdated
          )
        }

        if (this.closeBtn) this.closeBtn.addEventListener('click', this.boundClose)
        if (this.overlay) {
          this.overlay.addEventListener('click', e => {
            if (e.target === this.overlay) this.boundClose()
          })
        }

        this.initCollapsibleCards()
        this.initEngravingFileInputs()
        this.initFingerprintTabs()
        this.initEngravingSelection()
        this.initEngravingDoneButton()

        document.addEventListener('submit', this.boundFormSubmit, true)
        this.cartUpdateUnsub = subscribe(PUB_SUB_EVENTS.cartUpdate, evt => {
          if (evt.source === 'product-form') this.resetAll()
        })

        if (this.overlay) {
          document.body.appendChild(this.overlay)
        }

        const checked = this.querySelector('[data-engraving-toggle]:checked')
        if (checked) {
          requestAnimationFrame(() => this.handleToggleChange(checked))
        }
      }

      createCloneButton() {
        const real = this.realSubmitButton
        this.fakeButton = document.createElement('button')
        this.fakeButton.type = 'button'
        this.fakeButton.className = real.className
        this.fakeButton.setAttribute('data-engraving-trigger', '')
        this.fakeButton.innerHTML = `<span>${this.escapeHtml(this.buttonTextWhenYes)}</span>`
        this.fakeButton.style.display = 'none'
        this.fakeButton.addEventListener('click', e => {
          e.preventDefault()
          this.open()
        })
        real.parentNode.insertBefore(this.fakeButton, real.nextSibling)
      }

      escapeHtml(text) {
        const div = document.createElement('div')
        div.textContent = text
        return div.innerHTML
      }

      onKeydown(e) {
        if (e.key === 'Escape' && this.overlay && !this.overlay.hidden) this.close()
      }

      disconnectedCallback() {
        document.removeEventListener('keydown', this.boundKeydown)
        document.removeEventListener('submit', this.boundFormSubmit, true)
        if (this.cartUpdateUnsub) this.cartUpdateUnsub()
        if (this.overlay && this.overlay.parentNode === document.body) {
          this.overlay.remove()
        }
        const section = this.closest('section')
        if (section && this.boundFileUploadUpdated) {
          section.removeEventListener(
            'optional-file-upload-button-updated',
            this.boundFileUploadUpdated
          )
        }
        if (this.fakeButton && this.fakeButton.parentNode) {
          this.fakeButton.parentNode.removeChild(this.fakeButton)
        }
        if (this.realSubmitButton) {
          this.realSubmitButton.style.display = ''
        }
      }

      get isYesSelected() {
        const checked = this.querySelector('[data-engraving-toggle]:checked')
        return checked ? checked.value === 'yes' : false
      }

      fileUploadWantsClone() {
        try {
          const section = this.closest('section')
          const fileInput = section?.querySelector('optional-file-input')
          if (!fileInput || typeof fileInput.shouldShowFakeButton !== 'function') return false
          return fileInput.shouldShowFakeButton()
        } catch {
          return false
        }
      }

      updateButtonVisibility() {
        if (!this.realSubmitButton || !this.fakeButton) return
        if (!this.isYesSelected && this.engravingVariantInput) {
          this.engravingVariantInput.value = ''
        }
        if (this.fileUploadWantsClone()) {
          this.fakeButton.style.display = 'none'
          return
        }
        if (this.isYesSelected) {
          this.realSubmitButton.style.display = 'none'
          this.fakeButton.style.display = ''
        } else {
          this.realSubmitButton.style.display = ''
          this.fakeButton.style.display = 'none'
        }
      }

      handleToggleChange(input) {
        this.updateButtonVisibility()
      }

      initCollapsibleCards() {
        const root = this.overlay || this
        const triggers = root.querySelectorAll('[data-engraving-card-trigger]')
        triggers.forEach(trigger => {
          trigger.addEventListener('click', () => {
            const card = trigger.closest('[data-engraving-card]')
            const fields = card?.querySelector('[data-engraving-card-fields]')
            if (!card || !fields) return

            const isAlreadySelected = card.classList.contains('backside-engraving-upsell--selected')
            if (isAlreadySelected) {
              trigger.setAttribute('aria-expanded', 'false')
              fields.hidden = true
              card.classList.remove('backside-engraving-upsell--selected')
              this.setSelectedCard(null)
              return
            }

            root.querySelectorAll('[data-engraving-card]').forEach(c => {
              const t = c.querySelector('[data-engraving-card-trigger]')
              const f = c.querySelector('[data-engraving-card-fields]')
              if (t && f) {
                const openThis = c === card
                t.setAttribute('aria-expanded', openThis)
                f.hidden = !openThis
                c.classList.toggle('backside-engraving-upsell--selected', openThis)
              }
            })
            this.setSelectedCard(card)
          })
        })
      }

      initEngravingFileInputs() {
        const root = this.overlay || this
        root.querySelectorAll('[data-engraving-file-section]').forEach(section => {
          const card = section.closest('[data-engraving-card]')
          const formId = this.formId
          const hiddenContainer = section.querySelector('[data-engraving-hidden-files]')
          if (!hiddenContainer || !formId) return

          const slotStates = new Map()

          section.querySelectorAll('[data-engraving-file-slot]').forEach(slot => {
            const slotNum = slot.dataset.engravingFileSlot
            const propBase = (slot.dataset.propBase || '').trim()
            const fileInput = slot.querySelector('input[data-engraving-file]')
            const thumbnailsEl = slot.querySelector('[data-engraving-thumbnails]')
            const btnEl = slot.querySelector('[data-engraving-file-button]')
            if (!fileInput) return

            let selectedFiles = []
            let thumbnailUrls = []

            const propNameForIndex = i => {
              if (propBase.includes('Image 2')) return `properties[${propBase} - File ${i + 1}]`
              if (selectedFiles.length === 1) {
                if (section.dataset.engravingSlots === '1') return `properties[${propBase}]`
                return `properties[${propBase}]`
              }
              if (section.dataset.engravingSlots === '2')
                return `properties[${propBase} - ${i + 1}]`
              return `properties[${propBase} ${i + 1}]`
            }

            const syncHidden = () => {
              hiddenContainer.querySelectorAll('input[type="file"]').forEach(el => {
                if ((el.name || '').includes(propBase)) el.remove()
              })
              selectedFiles.forEach((file, i) => {
                const inp = document.createElement('input')
                inp.type = 'file'
                inp.name = propNameForIndex(i)
                inp.setAttribute('form', formId)
                inp.style.cssText =
                  'position:absolute;width:1px;height:1px;opacity:0;overflow:hidden;'
                const dt = new DataTransfer()
                dt.items.add(file)
                inp.files = dt.files
                hiddenContainer.appendChild(inp)
              })
            }

            const renderThumbnails = () => {
              if (!thumbnailsEl) return
              thumbnailUrls.forEach(url => URL.revokeObjectURL(url))
              thumbnailUrls = []
              thumbnailsEl.innerHTML = ''
              selectedFiles.forEach((file, idx) => {
                const isImage = file.type.startsWith('image/')
                const el = document.createElement('div')
                el.className =
                  'backside-engraving-upsell__thumbnail' +
                  (isImage ? '' : ' backside-engraving-upsell__thumbnail--pdf')
                el.setAttribute('role', 'listitem')
                if (isImage) {
                  const url = URL.createObjectURL(file)
                  thumbnailUrls.push(url)
                  const img = document.createElement('img')
                  img.className = 'backside-engraving-upsell__thumbnail-img'
                  img.src = url
                  img.alt = file.name
                  el.appendChild(img)
                } else {
                  el.innerHTML =
                    '<span class="backside-engraving-upsell__thumbnail-icon" aria-hidden="true"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><path d="M14 2v6h6M16 13H8M16 17H8M10 9H8"/></svg></span>'
                }
                const removeBtn = document.createElement('button')
                removeBtn.type = 'button'
                removeBtn.className = 'backside-engraving-upsell__thumbnail-remove'
                removeBtn.setAttribute('aria-label', 'Remove file')
                removeBtn.innerHTML =
                  '<svg viewBox="0 0 12 12" fill="none" stroke="currentColor" stroke-width="2"><path d="M1 1l10 10M11 1L1 11"/></svg>'
                removeBtn.addEventListener('click', () => {
                  if (thumbnailUrls[idx]) URL.revokeObjectURL(thumbnailUrls[idx])
                  thumbnailUrls.splice(idx, 1)
                  selectedFiles.splice(idx, 1)
                  syncHidden()
                  renderThumbnails()
                  this.onCardInputChange(card)
                })
                el.appendChild(removeBtn)
                thumbnailsEl.appendChild(el)
              })
            }

            const onFilesSelected = () => {
              const newFiles = Array.from(fileInput.files || [])
              if (newFiles.length === 0) return
              selectedFiles = [...selectedFiles, ...newFiles]
              syncHidden()
              renderThumbnails()
              fileInput.value = ''
              this.onCardInputChange(card)
            }

            fileInput.addEventListener('change', onFilesSelected)
            slotStates.set(slotNum, {
              selectedFiles: () => selectedFiles,
              syncHidden,
              clear: () => {
                selectedFiles = []
                thumbnailUrls.forEach(u => URL.revokeObjectURL(u))
                thumbnailUrls = []
              }
            })
          })

          section.querySelectorAll('[data-engraving-upload-later]').forEach(cb => {
            cb.addEventListener('change', () => this.onCardInputChange(card))
          })
        })
      }

      /** Tabbed fingerprint input: Upload Now | Upload Later. Syncs hidden checkbox and updates CTA. */
      initFingerprintTabs() {
        const root = this.overlay || this
        if (!root) return
        root.querySelectorAll('[data-engraving-file-section][data-engraving-fingerprint-tabs]').forEach(section => {
          const card = section.closest('[data-engraving-card]')
          const tabButtons = section.querySelectorAll('[data-engraving-tab]')
          const panels = section.querySelectorAll('[data-engraving-panel]')
          const uploadLaterCheckbox = section.querySelector('input[data-engraving-upload-later]')
          if (!tabButtons.length || !panels.length || !uploadLaterCheckbox) return

          const switchToTab = (tabValue) => {
            tabButtons.forEach(btn => {
              const isActive = btn.getAttribute('data-engraving-tab') === tabValue
              btn.classList.toggle('backside-engraving-upsell__tab--active', isActive)
              btn.setAttribute('aria-selected', isActive)
            })
            panels.forEach(panel => {
              const match = panel.getAttribute('data-engraving-panel') === tabValue
              panel.classList.toggle('hidden', !match)
              panel.hidden = !match
              panel.setAttribute('aria-hidden', !match)
            })
            uploadLaterCheckbox.checked = tabValue === 'later'
            if (card) this.onCardInputChange(card)
          }

          tabButtons.forEach(btn => {
            btn.addEventListener('click', () => {
              const tab = btn.getAttribute('data-engraving-tab')
              if (tab) switchToTab(tab)
            })
          })
        })
      }

      initEngravingSelection() {
        const root = this.overlay || this
        this.engravingVariantInput = root.querySelector('[data-engraving-variant-id]')
        this.selectedPriceEl = root.querySelector('[data-engraving-selected-price]')
        const cards = root.querySelectorAll('[data-engraving-card]')
        cards.forEach(card => {
          card.querySelectorAll('input, select').forEach(el => {
            el.addEventListener('input', () => this.onCardInputChange(card))
            el.addEventListener('change', () => this.onCardInputChange(card))
          })
          this.initFontSelectForCard(card)
          this.initCharIndicators(card)
        })
      }

      initCharIndicators(card) {
        const textInputs = card.querySelectorAll('input[data-engraving-text]')
        const updateIndicator = input => {
          const label = input.closest('label')
          if (!label) return
          const indicator = label.querySelector('[data-engraving-char-indicator]')
          if (!indicator) return
          const max = input.getAttribute('maxlength') || 10
          const len = (input.value || '').length
          indicator.textContent = `${len}/${max}`
        }
        textInputs.forEach(input => {
          updateIndicator(input)
          input.addEventListener('input', () => updateIndicator(input))
        })
      }

      updateAllCharIndicators() {
        const root = this.overlay || this
        root.querySelectorAll('input[data-engraving-text]').forEach(input => {
          const label = input.closest('label')
          if (!label) return
          const indicator = label.querySelector('[data-engraving-char-indicator]')
          if (!indicator) return
          const max = input.getAttribute('maxlength') || 10
          const len = (input.value || '').length
          indicator.textContent = `${len}/${max}`
        })
      }

      initFontSelectForCard(card) {
        const fontSelect = card.querySelector('[data-engraving-font]')
        const textInputs = card.querySelectorAll('[data-engraving-text]')
        if (!fontSelect || !textInputs.length) return
        const applyFont = () => {
          const selected = fontSelect.options[fontSelect.selectedIndex]
          const fontFamily = selected?.dataset?.fontFamily || selected?.value || ''
          const style = fontFamily ? `"${fontFamily}", sans-serif` : ''
          textInputs.forEach(input => {
            input.style.fontFamily = style
          })
        }
        fontSelect.addEventListener('change', applyFont)
        if (fontSelect.value) applyFont()
      }

      /** Updates CTA disabled state and button text from selected option validity only. */
      updateCTAState() {
        const doneBtn = (this.overlay || this).querySelector('[data-engraving-done]')
        if (doneBtn) doneBtn.disabled = !this.isSubmitEnabled
        this.updateModalButtonText()
      }

      /**
       * Call when user selects a card (clicks the card trigger). Sets selected card and option id, then updates CTA.
       */
      setSelectedCard(card) {
        if (!card) {
          this.selectedCard = null
          this.selectedOptionId = null
          if (this.engravingVariantInput) this.engravingVariantInput.value = ''
          this.updateCTAState()
          return
        }
        const variantId = (card.dataset.variantId || '').trim()
        const upsellIndex = card.dataset.upsellIndex || ''
        const optionId = variantId || (upsellIndex ? `index-${upsellIndex}` : null)
        if (!optionId) return
        this.selectedCard = card
        this.selectedOptionId = optionId
        if (this.engravingVariantInput) this.engravingVariantInput.value = variantId || ''
        this.updateCTAState()
      }

      /** Called when an input changes inside a card; only re-run CTA logic if that card is the selected one. */
      onCardInputChange(card) {
        if (!card) return
        const variantId = (card.dataset.variantId || '').trim()
        const optionId =
          variantId || (card.dataset.upsellIndex ? `index-${card.dataset.upsellIndex}` : null)
        if (optionId === this.selectedOptionId) this.updateCTAState()
      }

      /** Reset all values and close. Call after successful add to cart. */
      resetAll() {
        const defaultYes = this.dataset.defaultYes === 'true'
        const root = this.overlay || this
        this.toggleInputs = this.querySelectorAll('[data-engraving-toggle]')
        this.toggleInputs?.forEach(input => {
          if (input.value === 'no') input.checked = !defaultYes
          else if (input.value === 'yes') input.checked = defaultYes
        })
        const engravingVariantEl = root.querySelector('[data-engraving-variant-id]')
        if (engravingVariantEl) engravingVariantEl.value = ''
        this.engravingVariantInput = engravingVariantEl || this.engravingVariantInput
        this.selectedCard = null
        this.selectedOptionId = null
        root.querySelectorAll('[data-engraving-card]').forEach(card => {
          const trigger = card.querySelector('[data-engraving-card-trigger]')
          const fields = card.querySelector('[data-engraving-card-fields]')
          if (trigger) trigger.setAttribute('aria-expanded', 'false')
          if (fields) fields.hidden = true
          card.classList.remove('backside-engraving-upsell--selected')
          card.querySelectorAll('input:not([type="radio"]), select').forEach(el => {
            el.value = ''
          })
          this.updateAllCharIndicators()
          card.querySelectorAll('[data-engraving-text]').forEach(input => {
            input.style.fontFamily = ''
          })
          card.querySelectorAll('[data-file-name]').forEach(el => {
            el.textContent = ''
          })
          card.querySelectorAll('[data-engraving-file-section]').forEach(sec => {
            sec.querySelectorAll('[data-engraving-hidden-files]').forEach(c => {
              c.innerHTML = ''
            })
            sec.querySelectorAll('input[data-engraving-file]').forEach(inp => {
              inp.value = ''
            })
            sec.querySelectorAll('[data-engraving-thumbnails]').forEach(el => {
              el.innerHTML = ''
            })
            sec.querySelectorAll('input[data-engraving-upload-later]').forEach(cb => {
              cb.checked = false
            })
            if (sec.hasAttribute('data-engraving-fingerprint-tabs')) {
              sec.querySelectorAll('[data-engraving-tab]').forEach((btn, i) => {
                const isUpload = btn.getAttribute('data-engraving-tab') === 'upload'
                btn.classList.toggle('backside-engraving-upsell__tab--active', isUpload)
                btn.setAttribute('aria-selected', isUpload)
              })
              sec.querySelectorAll('[data-engraving-panel]').forEach(panel => {
                const isUpload = panel.getAttribute('data-engraving-panel') === 'upload'
                panel.classList.toggle('hidden', !isUpload)
                panel.hidden = !isUpload
                panel.setAttribute('aria-hidden', !isUpload)
              })
            }
            sec.querySelectorAll('input[data-engraving-free-kit]').forEach(cb => {
              cb.checked = false
            })
          })
        })
        this.updateCTAState()
        this.close()
      }

      /** Property name prefix for the selected card only, e.g. "Engraving - Upsell 1" */
      getSelectedCardPropPrefix() {
        if (!this.selectedCard?.dataset?.upsellIndex) return null
        return `Engraving - Upsell ${this.selectedCard.dataset.upsellIndex}`
      }

      /** True if the key belongs to the selected card's properties */
      isSelectedCardProp(key) {
        const prefix = this.getSelectedCardPropPrefix()
        if (!prefix) return false
        const propName = key.startsWith('properties[') ? key.slice('properties['.length, -1) : key
        return propName.startsWith(prefix)
      }

      /**
       * Normalize a property name for display in the cart.
       * When the selected card has its own variant (separate engraving line item),
       * strip the "Engraving - Upsell {index} - " prefix so the customer only sees
       * the clean field label (e.g. "Text 1", "Font", "File", etc.).
       *
       * For engraving-on-main (no variant), we keep the full prefix to preserve
       * which upsell the property came from.
       */
      getDisplayPropName(rawPropName) {
        if (!rawPropName) return rawPropName
        if (this.selectedCardHasVariant && this.selectedCard?.dataset?.upsellIndex) {
          const idx = this.selectedCard.dataset.upsellIndex
          const prefix = `Engraving - Upsell ${idx} - `
          if (rawPropName.startsWith(prefix)) {
            return rawPropName.slice(prefix.length)
          }
        }
        return rawPropName
      }

      /**
       * Check if the selected card has any file values (File objects cannot be sent via JSON).
       */
      engravingItemHasFiles(form) {
        const formData = new FormData(form)
        for (const [key, value] of formData.entries()) {
          if (
            key.startsWith('properties[') &&
            value instanceof File &&
            this.isSelectedCardProp(key)
          ) {
            return true
          }
        }
        return false
      }

      /** Check if main product has a file (e.g. from optional-file-input). File must be sent via FormData. */
      mainItemHasFile(form) {
        const formData = new FormData(form)
        for (const [key, value] of formData.entries()) {
          if (
            key.startsWith('properties[') &&
            !key.includes('Engraving -') &&
            value instanceof File
          ) {
            return true
          }
        }
        return false
      }

      /**
       * Build FormData for main product. Includes _basket_group.
       * When includeSelectedCardProps is true, also includes the selected card's properties
       * (for engraving-on-main when card has no variant).
       */
      buildMainFormData(form, mainVariantId, quantity, basketGroupId, sections, sectionsUrl, includeSelectedCardProps = false) {
        const section = this.closest('section')
        const engravingKeyFilter = includeSelectedCardProps
          ? (key) => this.isSelectedCardProp(key)
          : null
        const engravingDisplayNameFn = includeSelectedCardProps
          ? (name) => this.getDisplayPropName(name)
          : null

        return CartAdd.buildMainFormData(form, {
          mainVariantId,
          quantity,
          sections,
          sectionsUrl,
          section,
          basketGroupId,
          skipAllEngraving: !includeSelectedCardProps,
          engravingKeyFilter,
          engravingDisplayNameFn,
        })
      }

      /**
       * Build FormData for engraving line item only (selected card's properties). Includes _basket_group.
       */
      buildEngravingFormData(form, engravingVariantId, basketGroupId, sections, sectionsUrl) {
        const formData = new FormData(form)
        const fd = new FormData()
        fd.append('id', engravingVariantId)
        fd.append('quantity', 1)
        fd.append('properties[_basket_group]', basketGroupId)

        const cardPrefix = this.getSelectedCardPropPrefix()
        const fullPrefix = cardPrefix ? cardPrefix + ' - ' : null

        for (const [key, value] of formData.entries()) {
          if (!key.startsWith('properties[') || !this.isSelectedCardProp(key)) continue
          const rawName = key.slice('properties['.length, -1)
          const displayName = fullPrefix && rawName.startsWith(fullPrefix)
            ? rawName.slice(fullPrefix.length)
            : rawName
          if (value instanceof File) {
            fd.append(`properties[${displayName}]`, value)
          } else if (value != null && value !== '') {
            fd.append(`properties[${displayName}]`, String(value))
          }
        }
        fd.append('sections', sections.join(','))
        fd.append('sections_url', sectionsUrl)
        return fd
      }

      /** True when the selected card has an associated variant (upsell product); false when engraving is added to main only. */
      get selectedCardHasVariant() {
        return !!(this.selectedCard?.dataset?.variantId || '').trim()
      }

      /**
       * Build cart items for add-from-modal: main product + other upsells (birthstone, file) + engraving upsell (if card has variant).
       * When selected card has no variant, engraving fields are merged into the main product.
       * All items get _basket_group. If engraving has file(s), engraving is added via FormData (or main FormData when engraving-on-main).
       * Returns { items, mainVariantId, basketGroupId, engravingFormData, mainFormData } or null.
       */
      buildItemsForModalAdd() {
        const form = document.getElementById(this.formId)
        if (!form) return null
        const section = this.closest('section')
        if (!section) return null

        const formData = new FormData(form)
        const mainVariantId = formData.get('id')
        const quantity = parseInt(formData.get('quantity') || '1', 10)
        if (!mainVariantId) return null

        const basketGroupId = CartAdd.generateBasketGroupId()
        const mainHasFile = this.mainItemHasFile(form)
        const cart = CartAdd.getCart()
        const { sections: sectionsArr, sectionsUrl } = CartAdd.getSectionsConfig(cart)

        const engravingProps = {}
        for (const [key, value] of formData.entries()) {
          if (!key.startsWith('properties[') || !value) continue
          if (!this.isSelectedCardProp(key)) continue
          if (!(value instanceof File)) {
            engravingProps[this.getDisplayPropName(key.slice('properties['.length, -1))] = value
          }
        }

        const mainPropsObj = CartAdd.buildMainProperties(formData, {
          section,
          skipAllEngraving: true,
          skipUpsellGroupProps: true,
          keyFilter: (key) => !this.isSelectedCardProp(key),
        })
        const mainProps = { _basket_group: basketGroupId, ...mainPropsObj }
        const mainItem = { id: parseInt(mainVariantId, 10), quantity }

        const appendUpsellAndFileItems = (items) => {
          CartAdd.getUpsellItems(section).forEach(item => {
            items.push({
              ...item,
              id: parseInt(item.id, 10),
              properties: { _basket_group: basketGroupId, ...(item.properties || {}) },
            })
          })
          const fileItem = CartAdd.getOptionalFileItem(section)
          if (fileItem) {
            items.push({ ...fileItem, properties: { _basket_group: basketGroupId } })
          }
        }

        if (!this.selectedCard) {
          mainItem.properties = mainProps
          let mainFormData = null
          const items = [mainItem]
          appendUpsellAndFileItems(items)
          if (mainHasFile) {
            mainFormData = this.buildMainFormData(
              form, mainVariantId, quantity, basketGroupId, sectionsArr, sectionsUrl, false
            )
          }
          return { items, mainVariantId, basketGroupId, engravingFormData: null, mainFormData }
        }

        const engravingOnMain = !this.selectedCardHasVariant
        mainItem.properties = engravingOnMain
          ? { ...mainProps, ...engravingProps }
          : mainProps

        const engravingHasFiles = this.engravingItemHasFiles(form)
        const needsMainFormData = mainHasFile || (engravingOnMain && engravingHasFiles)

        let mainFormData = null
        const items = []
        if (!needsMainFormData) {
          items.push(mainItem)
        } else {
          mainFormData = this.buildMainFormData(
            form, mainVariantId, quantity, basketGroupId, sectionsArr, sectionsUrl, engravingOnMain
          )
        }

        appendUpsellAndFileItems(items)
        this.pushFingerprintKitIfChecked(items, basketGroupId)

        if (engravingOnMain) {
          return { items, mainVariantId, basketGroupId, engravingFormData: null, mainFormData }
        }

        const engravingVariantId = parseInt(this.selectedCard.dataset.variantId, 10)
        if (!engravingVariantId) return null

        let engravingFormData = null
        if (engravingHasFiles) {
          engravingFormData = this.buildEngravingFormData(
            form, engravingVariantId, basketGroupId, sectionsArr, sectionsUrl
          )
        } else {
          items.push({
            id: engravingVariantId,
            quantity: 1,
            properties: { _basket_group: basketGroupId, ...engravingProps },
          })
        }

        return { items, mainVariantId, basketGroupId, engravingFormData, mainFormData }
      }

      /** If selected card is fingerprint/fingerprint_heart and "free kit" is checked, add fingerprint kit product to items. Only adds if not already in items (e.g. from optional-file-input linked product). */
      pushFingerprintKitIfChecked(items, basketGroupId) {
        const kitVariantId = (this.dataset.fingerprintKitVariantId || '').trim()
        if (!this.selectedCard || !kitVariantId) return
        const type = this.selectedCard.dataset.upsellType || ''
        if (type !== 'fingerprint' && type !== 'fingerprint_heart') return
        if (!this.selectedCard.querySelector('[data-engraving-free-kit]')?.checked) return
        const kitId = parseInt(kitVariantId, 10)
        if (items.some(item => item.id === kitId)) return
        items.push({
          id: kitId,
          quantity: 1,
          properties: { _basket_group: basketGroupId }
        })
      }

      doAddToCartFromModal() {
        const result = this.buildItemsForModalAdd()
        if (!result) return
        const { items, mainVariantId, engravingFormData, mainFormData } = result
        const cart = CartAdd.getCart()
        const form = document.getElementById(this.formId)
        const productFormEl = form?.closest('product-form')
        const doneBtn = (this.overlay || this).querySelector('[data-engraving-done]')

        if (doneBtn) {
          doneBtn.disabled = true
          doneBtn.classList.add('loading')
          const doneSpan = doneBtn.querySelector('span')
          const spinner = doneBtn.querySelector('.loading__spinner')
          if (doneSpan) doneSpan.classList.add('hidden')
          if (spinner) spinner.classList.remove('hidden')
        }

        const { sections, sectionsUrl } = CartAdd.getSectionsConfig(cart)
        if (cart) cart.setActiveElement(document.activeElement)

        const finish = response => {
          CartAdd.handleResponse(response, {
            cart, productFormEl, mainVariantId,
            onSuccess: () => this.resetAll(),
          })
        }

        const finallyCb = () => {
          if (doneBtn) {
            doneBtn.classList.remove('loading')
            const doneSpan = doneBtn.querySelector('span')
            const spinner = doneBtn.querySelector('.loading__spinner')
            if (doneSpan) doneSpan.classList.remove('hidden')
            if (spinner) spinner.classList.add('hidden')
          }
          this.updateCTAState()
        }

        if (mainFormData) {
          CartAdd.postFormData(mainFormData)
            .then(response => {
              if (response.status) { finish(response); return Promise.reject() }
              if (items.length > 0) return CartAdd.postJSON(items, sections, sectionsUrl)
              return null
            })
            .then(response => {
              if (response === undefined) return
              if (response && response.status) { finish(response); return Promise.reject() }
              if (engravingFormData) return CartAdd.postFormData(engravingFormData)
              if (response) finish(response)
              return null
            })
            .then(response => { if (response) finish(response) })
            .catch(err => { if (err) console.error(err) })
            .finally(finallyCb)
          return
        }

        if (engravingFormData) {
          CartAdd.postJSON(items, sections, sectionsUrl)
            .then(response => {
              if (response.status) { finish(response); return }
              return CartAdd.postFormData(engravingFormData)
            })
            .then(linked => { if (linked) finish(linked) })
            .catch(console.error)
            .finally(finallyCb)
          return
        }

        CartAdd.postJSON(items, sections, sectionsUrl)
          .then(finish)
          .catch(console.error)
          .finally(finallyCb)
      }

      initEngravingDoneButton() {
        const doneBtn = (this.overlay || this).querySelector('[data-engraving-done]')
        if (!doneBtn) return
        doneBtn.disabled = true
        doneBtn.addEventListener('click', () => {
          if (!this.isSubmitEnabled) return
          this.doAddToCartFromModal()
        })
      }

      onFormSubmit(e) {
        const form = e.target
        if (!form || form.getAttribute('data-type') !== 'add-to-cart-form') return
        const section = this.closest('section')
        if (!section || !section.contains(form)) return

        const validation = CartAdd.validateInputModal(section, form)
        if (!validation.valid) {
          e.preventDefault(); e.stopPropagation(); e.stopImmediatePropagation()
          validation.inputModal.openAndShowErrors(validation.invalidFields)
          return
        }

        const root = this.overlay || this
        const engravingInput = root.querySelector('[data-engraving-variant-id]')
        const engravingVariantIdVal = engravingInput?.value?.trim()
        if (!engravingVariantIdVal) return

        this.selectedCard =
          root.querySelector(`[data-engraving-card][data-variant-id="${engravingVariantIdVal}"]`) ||
          null

        e.preventDefault()
        e.stopPropagation()
        e.stopImmediatePropagation()

        const productFormEl = form.closest('product-form')
        const submitButton = productFormEl?.querySelector('[type="submit"]')
        const cart = CartAdd.getCart()

        CartAdd.setButtonLoading(submitButton, productFormEl, true)

        const formData = new FormData(form)
        const mainVariantId = formData.get('id')
        const quantity = parseInt(formData.get('quantity') || '1', 10)
        const engravingVariantId = parseInt(engravingVariantIdVal, 10)
        if (!engravingVariantId) return

        const basketGroupId = CartAdd.generateBasketGroupId()
        const mainPropsObj = CartAdd.buildMainProperties(formData, {
          section,
          skipAllEngraving: true,
          skipUpsellGroupProps: true,
          skipVariantProp: false,
        })
        const mainItem = {
          id: parseInt(mainVariantId, 10),
          quantity,
          properties: Object.keys(mainPropsObj).length > 0
            ? { _basket_group: basketGroupId, ...mainPropsObj }
            : { _basket_group: basketGroupId },
        }

        const engravingHasFiles = this.engravingItemHasFiles(form)
        const { sections, sectionsUrl } = CartAdd.getSectionsConfig(cart)
        if (cart) cart.setActiveElement(document.activeElement)

        const finish = response => {
          CartAdd.handleResponse(response, { cart, productFormEl, mainVariantId })
        }
        const finallyCb = () => CartAdd.setButtonLoading(submitButton, productFormEl, false)

        const charmItems = CartAdd.getCharmItems(section)

        if (engravingHasFiles) {
          CartAdd.postJSON([mainItem, ...charmItems], sections, sectionsUrl)
            .then(response => {
              if (response.status) { finish(response); return }
              const engravingFormData = this.buildEngravingFormData(
                form, engravingVariantId, basketGroupId, sections, sectionsUrl
              )
              return CartAdd.postFormData(engravingFormData)
            })
            .then(linked => { if (linked) finish(linked) })
            .catch(console.error)
            .finally(finallyCb)
          return
        }

        const engravingProps = {}
        for (const [key, value] of formData.entries()) {
          if (key.startsWith('properties[') && value && this.isSelectedCardProp(key)) {
            engravingProps[this.getDisplayPropName(key.slice('properties['.length, -1))] = value
          }
        }
        const engravingItem = {
          id: engravingVariantId,
          quantity: 1,
          properties: { _basket_group: basketGroupId, ...engravingProps },
        }

        CartAdd.postJSON([mainItem, engravingItem, ...charmItems], sections, sectionsUrl)
          .then(finish)
          .catch(console.error)
          .finally(finallyCb)
      }

      open() {
        if (!this.overlay) return
        this.selectedCard = null
        this.selectedOptionId = null
        if (this.engravingVariantInput) this.engravingVariantInput.value = ''
        ;(this.overlay || this).querySelectorAll('[data-engraving-card]').forEach(c => {
          const t = c.querySelector('[data-engraving-card-trigger]')
          const f = c.querySelector('[data-engraving-card-fields]')
          if (t) t.setAttribute('aria-expanded', 'false')
          if (f) f.hidden = true
          c.classList.remove('backside-engraving-upsell--selected')
        })
        this.updateCTAState()
        this.overlay.hidden = false
        this.overlay.setAttribute('data-open', 'false')
        document.body.style.overflow = 'hidden'
        document.addEventListener('keydown', this.boundKeydown)
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            this.overlay.setAttribute('data-open', 'true')
          })
        })
      }

      close() {
        if (!this.overlay) return
        this.overlay.setAttribute('data-open', 'false')
        document.removeEventListener('keydown', this.boundKeydown)
        this.selectedCard = null
        this.selectedOptionId = null
        if (this.engravingVariantInput) this.engravingVariantInput.value = ''
        ;(this.overlay || this).querySelectorAll('[data-engraving-card]').forEach(card => {
          const trigger = card.querySelector('[data-engraving-card-trigger]')
          const fields = card.querySelector('[data-engraving-card-fields]')
          if (trigger) trigger.setAttribute('aria-expanded', 'false')
          if (fields) fields.hidden = true
          card.classList.remove('backside-engraving-upsell--selected')
          card.querySelectorAll('input:not([type="radio"]), select').forEach(el => {
            el.value = ''
          })
          this.updateAllCharIndicators()
          card.querySelectorAll('[data-file-name]').forEach(el => {
            el.textContent = ''
          })
        })
        this.updateCTAState()
        setTimeout(() => {
          this.overlay.hidden = true
          document.body.style.overflow = ''
        }, 350)
      }
    }
  )
}
